import {ThumbsupIcon, ThumbsdownIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useCallback, useEffect, useState} from 'react'
import capitalize from 'lodash-es/capitalize'
import {useFeedbackDialog} from './useFeedbackDialog'
import {useSessionContext} from '../../../contexts/SessionContext'
import styles from './SessionFeedbackButtons.module.css'
import {type RatingOption, thumbsDownIcon, thumbsUpIcon} from '@github-ui/user-feedback'

const ratingOptions: Array<RatingOption<'NEGATIVE' | 'POSITIVE'>> = [
  {
    name: 'Bad',
    value: 'NEGATIVE' as const,
    icon: thumbsDownIcon,
    color: 'bad',
  },
  {
    name: 'Good',
    value: 'POSITIVE' as const,
    icon: thumbsUpIcon,
    color: 'good',
  },
]

type Rating = (typeof ratingOptions)[number]['value']

export function SessionFeedbackButtons() {
  const {session} = useSessionContext()

  const [submittedFeedback, setSubmittedFeedback] = useState<Rating | null>(null)
  const [currentSessionId, setCurrentSessionId] = useState(session.id)

  // Reset feedback state when session changes
  useEffect(() => {
    if (currentSessionId !== session.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Intentionally resetting state when session changes
      setSubmittedFeedback(null)
      setCurrentSessionId(session.id)
    }
  }, [session.id, currentSessionId])

  const {openFeedback, FeedbackDialog} = useFeedbackDialog(`session-log:session_id=${session.id}`, ratingOptions)
  const onClick = useCallback(
    (rating: Rating) => {
      setSubmittedFeedback(rating) // Feedback isn't submitted at this point, but we're optimistically updating the UI
      openFeedback(rating)
    },
    [openFeedback],
  )

  const onFeedbackSubmitted = useCallback((rating: Rating) => setSubmittedFeedback(rating), [])
  const onFeedbackDialogClose = useCallback((reason: 'submit' | 'cancel' | 'dismiss') => {
    if (reason === 'cancel') {
      // User cancelled - reset to allow them to try again
      setSubmittedFeedback(null)
    }
    // For 'submit' and 'dismiss', keep the submitted feedback state
  }, [])

  return (
    <div className={styles.container}>
      {submittedFeedback != null ? (
        <IconButton
          aria-label={`${capitalize(submittedFeedback)} feedback submitted`}
          icon={submittedFeedback === 'POSITIVE' ? ThumbsupIcon : ThumbsdownIcon}
          variant="invisible"
          size="small"
          inactive
          aria-disabled
        />
      ) : (
        <>
          <IconButton
            icon={ThumbsupIcon}
            aria-label="Positive feedback"
            onClick={() => onClick('POSITIVE')}
            variant="invisible"
            size="small"
          />
          <IconButton
            icon={ThumbsdownIcon}
            aria-label="Negative feedback"
            onClick={() => onClick('NEGATIVE')}
            variant="invisible"
            size="small"
          />
        </>
      )}
      <FeedbackDialog onClose={onFeedbackDialogClose} onSubmitted={onFeedbackSubmitted} />
    </div>
  )
}
